import { useEffect, useState } from "react";
import { Button, Card, Checkbox, Cluster, FormField, Input, Stack } from "@salesapp/ui";
import { TipHeading } from "./AdminTip";
import { Flash } from "./Flash";
import { api } from "./api";
import { notifySaved } from "./savedToast";
import { formatUtc } from "./format";

export interface CustomerAlerts {
  recipients: string;
  notify_trial: boolean;
  notify_conversion: boolean;
  updated_at: string | null;
  updated_by: string | null;
}

export function CustomerAlertsCard() {
  const [alerts, setAlerts] = useState<CustomerAlerts | null>(null);
  const [recipients, setRecipients] = useState("");
  const [notifyTrial, setNotifyTrial] = useState(false);
  const [notifyConversion, setNotifyConversion] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);
  const [savedAt, setSavedAt] = useState<string | null>(null);

  useEffect(() => {
    void (async () => {
      try {
        const row = await api.get<CustomerAlerts>("/operator/customer-alerts");
        setAlerts(row);
        setRecipients(row.recipients ?? "");
        setNotifyTrial(Boolean(row.notify_trial));
        setNotifyConversion(Boolean(row.notify_conversion));
        setSavedAt(row.updated_at);
      } catch (err) {
        setError(err instanceof Error ? err.message : "Trial email settings could not be loaded.");
      }
    })();
  }, []);

  async function save() {
    setSaving(true);
    setError(null);
    try {
      const row = await api.put<CustomerAlerts>("/operator/customer-alerts", {
        recipients: recipients.trim(),
        notify_trial: notifyTrial,
        notify_conversion: notifyConversion,
      });
      setAlerts(row);
      setRecipients(row.recipients ?? "");
      setNotifyTrial(Boolean(row.notify_trial));
      setNotifyConversion(Boolean(row.notify_conversion));
      setSavedAt(row.updated_at);
      notifySaved("Signup emails saved.");
    } catch (err) {
      setError(err instanceof Error ? err.message : "Save failed");
    } finally {
      setSaving(false);
    }
  }

  const ready = alerts !== null;

  return (
    <Stack gap={4}>
      <TipHeading
        as="h2"
        label="Trial and conversion emails"
        tip="Sends a short notice when a new customer signs up, or when a trial customer pays and becomes a subscription. Each notice includes the company name, the org admin email that registered, and the UTC time it completed. The addresses are stored for the platform and stay in place after a deploy. Leave the address box blank, or uncheck an option, and that notice is not sent."
      />
      {error && <Flash tone="error">{error}</Flash>}
      <Card>
        <Stack gap={3}>
          <FormField
            label="Email addresses"
            htmlFor="customer-alert-recipients"
            tooltip="Comma-separated. Example: sales@company.com, ops@company.com. Leave blank to send nothing."
            hint="Separate addresses with a comma."
          >
            <Input
              id="customer-alert-recipients"
              autoComplete="off"
              placeholder="sales@company.com, ops@company.com"
              value={recipients}
              disabled={!ready || saving}
              onChange={(event) => setRecipients(event.target.value)}
            />
          </FormField>
          <Checkbox
            label="Email me on a new signup"
            tooltip="Checked: email the addresses above when a new customer registers, whether they start a trial or pay at signup."
            checked={notifyTrial}
            disabled={!ready || saving}
            onChange={(event) => setNotifyTrial(event.target.checked)}
          />
          <Checkbox
            label="Notify on Trial customer conversion"
            tooltip="Checked: email the addresses above when a trial customer completes a paid subscription."
            checked={notifyConversion}
            disabled={!ready || saving}
            onChange={(event) => setNotifyConversion(event.target.checked)}
          />
          <Cluster gap={3} align="center">
            <Button variant="secondary" disabled={!ready || saving} onClick={() => void save()}>
              {saving ? "Saving…" : "Save"}
            </Button>
            {savedAt ? <p className="admin-muted">Last changed {formatUtc(savedAt)}</p> : null}
          </Cluster>
        </Stack>
      </Card>
    </Stack>
  );
}
